/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    FormControlLabel,
    Switch,
    InputAdornment,
    IconButton,
    Tooltip,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";

import { useParams, useLocation } from "react-router-dom";
import apiHeaders from "./apiHeaders"
// import { handleSessionExpiredClose } from "./logout"; // Assuming this might be used later
import { setRilletUserId } from "./storageUtils";

function ZampSettings() {
    const params = useParams();
    const location = useLocation();

    // Zamp API Token States
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Rillet API Key States
    const [rilletKey, setRilletKey] = useState("");
    const [rilletError, setRilletError] = useState("");
    const [rilletSuccessMessage, setRilletSuccessMessage] = useState("");

    // Webhook URL (read-only) — appUrl/api/webhook/{webhookGuid}.
    // The config identifier stored in sessionStorage is the webhook guid.
    const [webhookUrl, setWebhookUrl] = useState("");
    const [webhookCopied, setWebhookCopied] = useState(false);

    useEffect(() => {
        if (params && params.rilletUserId && params.rilletUserId !== ":rilletUserId") {
            setRilletUserId(params.rilletUserId);
            window.dispatchEvent(new Event("RilletUserIdChange"));
        }
    }, [location.search, params.rilletUserId]);

    // Build the webhook URL from the current webhook guid, and keep it in sync.
    useEffect(() => {
        const computeWebhookUrl = () => {
            const webhookGuid = sessionStorage.getItem("rilletUserId");
            setWebhookUrl(
                webhookGuid
                    ? `${process.env.REACT_APP_CONNECTOR_API_URL}/api/webhook/${webhookGuid}`
                    : ""
            );
        };
        computeWebhookUrl();
        window.addEventListener("RilletUserIdChange", computeWebhookUrl);
        return () => window.removeEventListener("RilletUserIdChange", computeWebhookUrl);
    }, []);

    const handleCopyWebhook = async () => {
        if (!webhookUrl) return;
        try {
            await navigator.clipboard.writeText(webhookUrl);
            setWebhookCopied(true);
            setTimeout(() => setWebhookCopied(false), 2000);
        } catch {
            // clipboard unavailable — no-op
        }
    };


    // --- Zamp Token Handlers ---
    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        if (error) setError("");
    };

    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        const token = password;
        const data = {
            token,
            rilletUserId: sessionStorage.getItem("rilletUserId"),
        };

        if (password.length !== 64) {
            setSuccessMessage("")
            setError("API token must be exactly 64 characters.");
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/zamp`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                    },
                    body: JSON.stringify(data),
                }
            );

            const result = await response.json();
            if (response.ok) {
                setSuccessMessage(result.message);
                setError("");
            } else {
                setError(`${result.message}`);
                setSuccessMessage("");
            }
        } catch (err) {
            setError("Internal server error. Please try again.");
            setSuccessMessage("");
        }
    };

    // --- Rillet API Key Handlers ---
    const handleRilletKeyChange = (event) => {
        setRilletKey(event.target.value);
        if (rilletError) setRilletError("");
    };

    const handleRilletSubmit = async (event) => {
        event.preventDefault();
        // Trim so a stray space/newline from pasting the key can't cause a false "Invalid Api Key".
        const token = rilletKey?.trim();
        const data = {
            token,
            rilletUserId: sessionStorage.getItem("rilletUserId"),
        };

        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/rillet`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                    },
                    body: JSON.stringify(data),
                }
            );

            const result = await response.json();
            if (response.ok) {
                setRilletSuccessMessage(result.message);
                setRilletError("");
            } else {
                setRilletError(`${result.message}`);
                setRilletSuccessMessage("");
            }
        } catch (err) {
            setRilletError("Internal server error. Please try again.");
            setRilletSuccessMessage("");
        }
    };


    // --- Settings States & Initialization ---
    const [loadingOptions, setLoadingOptions] = useState(false);

    // Toggle option states
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [calculationsEnabled, setCalculationsEnabled] = useState(false);
    const [loggingEnabled, setLoggingEnabled] = useState(false);
    const [recalculateEnabled, setRecalculateEnabled] = useState(false);

    // Populating stored user options
    useEffect(() => {
        const fetchZampToken = async () => {
            if (sessionStorage.getItem('rilletUserId')) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/zamp/token?rilletUserId=${sessionStorage.getItem('rilletUserId')}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                            }
                        }
                    );
                    const data = await response.json();
                    if (data?.token) {
                        setPassword(data.token);
                    }
                } catch (error) {
                    setError(error.message);
                }
            }
        };

        const fetchRilletToken = async () => {
            if (sessionStorage.getItem('rilletUserId')) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/rillet/token?rilletUserId=${sessionStorage.getItem('rilletUserId')}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                            }
                        }
                    );
                    const data = await response.json();
                    if (data?.token) {
                        setRilletKey(data.token);
                    }
                } catch (error) {
                    setRilletError(error.message);
                }
            }
        };

        const fetchOptions = async () => {
            setLoadingOptions(true);
            if (sessionStorage.getItem('rilletUserId')) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/settings?rilletUserId=${sessionStorage.getItem('rilletUserId')}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                            }
                        }
                    );
                    const data = await response.json();
                    setSyncEnabled(data["isSyncEnabled"]);
                    setCalculationsEnabled(data["isCalculationsEnabled"]);
                    setLoggingEnabled(data["isLoggingEnabled"]);
                    setRecalculateEnabled(data["isRecalculateEnabled"]);
                } catch (error) {
                    // Handle fetch error
                } finally {
                    setLoadingOptions(false);
                }
            }
        };
        
        fetchOptions();
        fetchZampToken();
        fetchRilletToken();
    }, []);

    const [saveSettingserror, setSaveSettingsError] = useState("");
    const [saveSettingsSuccessMessage, setSaveSettingsSuccessMessage] = useState("");

    // Handle custom user settings submit
    const handleSubmit = async (event) => {
        event.preventDefault()

        const data = {
            rilletUserId: sessionStorage.getItem("rilletUserId"),
            isSyncEnabled: syncEnabled,
            isCalculationsEnabled: calculationsEnabled,
            isLoggingEnabled: loggingEnabled,
            isRecalculateEnabled: recalculateEnabled,
        };
        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/settings`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                    },
                    body: JSON.stringify(data),
                }
            );

            if (response.ok) {
                setSaveSettingsSuccessMessage("Settings saved successfully!")
                setSaveSettingsError("");
            } else {
                setSaveSettingsError("Failed to save settings.");
                setSaveSettingsSuccessMessage("");
            }
        } catch (err) {
            setSaveSettingsError("Network Error: Unable to save settings. Please try again later.");
            setSaveSettingsSuccessMessage("");
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Box sx={{ alignItems: "center" }}>
                <Typography
                    className="zamp-page-title"
                    sx={{ mb: 2, textAlign: "center" }}
                >
                    Zamp Tax Settings
                </Typography>
            </Box>

            {/* Zamp API Token Form */}
            <Box component="form" onSubmit={handleTokenSubmit}>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
                        <TextField
                            label="Zamp API Token"
                            type="password"
                            variant="outlined"
                            fullWidth
                            required
                            value={password}
                            onChange={handlePasswordChange}
                            name="password"
                        />
                        {error && <Typography color="error">{error}</Typography>}
                        {successMessage && (
                            <Typography color="success">{successMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Update Zamp API Token
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            {/* Rillet API Key Form */}
            <Box component="form" onSubmit={handleRilletSubmit}>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
                        <TextField
                            label="Rillet API Key"
                            type="password"
                            variant="outlined"
                            fullWidth
                            required
                            value={rilletKey}
                            onChange={handleRilletKeyChange}
                            name="rilletKey"
                        />
                        {rilletError && <Typography color="error">{rilletError}</Typography>}
                        {rilletSuccessMessage && (
                            <Typography color="success">{rilletSuccessMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Update Rillet API Key
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            {/* Webhook URL (read-only) */}
            <Box>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
                        <TextField
                            label="Webhook URL"
                            variant="outlined"
                            fullWidth
                            value={webhookUrl}
                            placeholder="Available after the customer is configured"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy webhook URL">
                                            <span>
                                                <IconButton onClick={handleCopyWebhook} edge="end" disabled={!webhookUrl}>
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {webhookCopied && <Typography color="success">Copied!</Typography>}
                    </Stack>
                </Paper>
            </Box>

            {/* Application Settings Form */}
            <Box component="form" onSubmit={handleSubmit}>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    {loadingOptions && <Typography>Loading options...</Typography>}

                    <Stack spacing={3} direction="column" sx={{ width: "100%", mt: 2 }}>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={calculationsEnabled} onChange={() => setCalculationsEnabled(!calculationsEnabled)} />
                                }
                                label="Allow Zamp to handle tax calculations"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Enable Zamp to automatically calculate tax on transactions.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={syncEnabled} onChange={() => {
                                        const newVal = !syncEnabled;
                                        setSyncEnabled(newVal);
                                        if (!newVal) setRecalculateEnabled(false);
                                    }} />
                                }
                                label="Sync transactions with Zamp"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Required for all transactions including historical transaction sync.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={loggingEnabled} onChange={() => setLoggingEnabled(!loggingEnabled)} />
                                }
                                label="Retain Zamp logs for 30 days"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Enable debug logging for troubleshooting. Logs are retained for 30 days.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={recalculateEnabled} disabled={!syncEnabled} onChange={() => setRecalculateEnabled(!recalculateEnabled)} />
                                }
                                label="Enable transaction sync recalculate"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Recalculate transactions for Zamp. Requires "Sync transactions" to be enabled.
                            </Typography>
                        </Box>
                        {saveSettingserror && <Typography color="error">{saveSettingserror}</Typography>}
                        {saveSettingsSuccessMessage && (
                            <Typography color="success">{saveSettingsSuccessMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Save Settings
                        </Button>
                    </Stack>
                </Paper>
            </Box>

        </Box>
    );
}

export default ZampSettings;